import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTheme } from '../theme';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';
import { LineItemsList } from './TicketCard';
import { useLineItemEditor } from './LineItemEditor';
import { RequestEstimateDialog, EstimateRequestBanner } from './EstimateRequest';

// ── Bench view ────────────────────────────────────────────────────────────────
//
// Tech Mode: one ticket, full screen, for a tech at the stand. Big type and big
// targets so it works on a tablet with dirty hands. No texting panel: that
// stays in the Service Queue's ticket.
//
// Laid out to the mockup Manny signed off on 2 Oct ("Bench View Mockup"
// canvas), top to bottom:
//
//   header       which ticket this is (orange ticket badge, customer in gold, blue on light,
//                bike and colour) beside the timer, the two halves equal
//   notes        receipt note left, internal note right, always the same size
//                whatever is in them; no Save buttons, both save themselves
//   line items   the same add / edit / remove controls as the Service Queue's
//                ticket (LineItemEditor.jsx, LineItemsList), sized up
//   history      the customer's last finished tickets, collapsed; greyed out
//                rather than removed when there are none
//   action bar   Run checklist and Move to next status, pinned to the bottom
//
// The timer's rules live on the server (helpers/techTimer.js). This screen
// shows the state the server returns and counts the seconds locally between
// calls, anchored to the server's clock so a tablet with the wrong time still
// shows the right elapsed time.

// Same list the server uses to call a ticket finished (helpers/lightspeed.js).
// Line kinds in the service history, coloured like the line items above.
const HISTORY_KIND = {
  Labor: { fg: '#3a8fd4', bg: '#e3effa', bgDark: '#14283d' },
  Parts: { fg: '#27ae60', bg: '#e1f4e8', bgDark: '#12301f' },
  Misc: { fg: '#9b6bd1', bg: '#efe6fa', bgDark: '#271d38' },
};
const FINISHED = ['done & paid', 'finished', 'finished/paid', 'invoiced'];
const FINISH_NAMES = ['finished', 'finished/paid', 'invoiced'];

// The receipt note saves this long after the last keystroke, and at once when
// the box loses focus.
const RECEIPT_SAVE_DELAY_MS = 1200;

// The large type on this screen. Loaded in public/index.html.
const DISPLAY_FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

function hms(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
function hm(totalSeconds) {
  const mins = Math.round(Math.max(0, totalSeconds || 0) / 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// The internal note is one Lightspeed text field that Spinboard prepends
// entries to, each under a "[Internal Note — who — when]" header (Ticket
// Detail writes the same format). Split it back into entries to list them;
// anything before the first header, or a note with no headers at all, is
// shown as one entry without a byline.
function splitInternalNote(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  // \u2014 is the em dash in the header. Escaped so the pattern survives any
  // re-encoding of the bundle.
  const re = /^\[Internal Note \u2014 (.+?) \u2014 (.+?)\]\s*$/gm;
  const heads = [];
  let m;
  while ((m = re.exec(raw)) !== null) heads.push({ index: m.index, end: re.lastIndex, who: m[1], when: m[2] });
  if (!heads.length) return [{ who: null, when: null, body: raw }];
  const out = [];
  if (heads[0].index > 0) {
    const lead = raw.slice(0, heads[0].index).trim();
    if (lead) out.push({ who: null, when: null, body: lead });
  }
  heads.forEach((h, i) => {
    const body = raw.slice(h.end, i + 1 < heads.length ? heads[i + 1].index : raw.length).trim();
    out.push({ who: h.who, when: h.when, body });
  });
  return out;
}

// ── The ticket's timer sessions, to check and correct (2 Oct) ─────────────────
//
// A manager sees and may change everyone's sessions on the ticket; a tech sees
// their own and may change the ones from today. The server decides (helpers/
// techTimer.js canEditSession) and says per session whether the caller may;
// this only shows it. Every change rewrites the tech's time in Lightspeed.
// "Add time" is for work done without the timer running.

// <input type="datetime-local"> speaks local "YYYY-MM-DDTHH:MM".
const toLocalInput = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const durationOf = (a, b) => {
  const mins = Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};
const fmtWhen = (d) => new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

function SessionsDialog({ ticketId, displayId, T, isDark, onClose, onChanged }) {
  const [data, setData] = useState(null);           // GET .../sessions
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(null);     // session id, or 'new'
  const [form, setForm] = useState({ start: '', end: '', userId: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/timer/ticket/${ticketId}/sessions`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load the sessions.');
      setData(d);
    } catch (err) { setError(err.message); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const send = async (method, url, body, done) => {
    setBusy(true); setError(''); setNote('');
    try {
      const r = await fetch(`${API}${url}`, { method, credentials: 'include', headers: authHeaders(true), body: body ? JSON.stringify(body) : undefined });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That did not save.');
      if (d.written === false) setNote(`Saved in Spinboard, but Lightspeed did not take the new time: ${d.writeError}. Use Retry on the timer.`);
      else setNote(done);
      setEditing(null); setConfirmDelete(null);
      onChanged(d);
      await load();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const startEdit = (s) => { setEditing(s.id); setConfirmDelete(null); setForm({ start: toLocalInput(s.startedAt), end: toLocalInput(s.endedAt), userId: '' }); };
  const startAdd = () => {
    const end = new Date(); end.setSeconds(0, 0);
    const start = new Date(end.getTime() - 30 * 60000);
    setEditing('new'); setConfirmDelete(null);
    setForm({ start: toLocalInput(start), end: toLocalInput(end), userId: '' });
  };

  const input = { background: T.input, color: T.text, border: `1px solid ${T.inputBorder}`, borderRadius: 8, padding: '7px 9px', fontSize: 14, fontFamily: 'inherit' };
  const btn = (kind) => ({
    fontSize: 14, fontWeight: 600, padding: '0 14px', minHeight: 38, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
    ...(kind === 'primary' ? { background: '#2a7ab8', color: '#fff', border: 'none' }
      : kind === 'danger' ? { background: '#c0392b', color: '#fff', border: 'none' }
      : { background: 'transparent', color: T.text, border: `1px solid ${T.inputBorder}` }),
  });
  const total = (data?.sessions || []).reduce((sum, s) => sum + (s.endedAt ? (new Date(s.endedAt) - new Date(s.startedAt)) : 0), 0) / 1000;

  const editor = (onSave, saveLabel) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
      {editing === 'new' && data?.isManager && data.people.length > 0 && (
        <label style={{ fontSize: 13, color: T.textSub }}>For{' '}
          <select value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} style={input}>
            <option value="">Me</option>
            {data.people.map(p => <option key={p.userId} value={p.userId}>{p.name}</option>)}
          </select>
        </label>
      )}
      <label style={{ fontSize: 13, color: T.textSub }}>Start{' '}
        <input type="datetime-local" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} style={input} />
      </label>
      <label style={{ fontSize: 13, color: T.textSub }}>End{' '}
        <input type="datetime-local" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} style={input} />
      </label>
      {form.start && form.end && new Date(form.end) > new Date(form.start) && (
        <span style={{ fontSize: 13, color: T.textSub }}>{durationOf(form.start, form.end)}</span>
      )}
      <span style={{ display: 'inline-flex', gap: 8, marginLeft: 'auto' }}>
        <button disabled={busy || !form.start || !form.end} onClick={onSave} style={btn('primary')}>{busy ? 'Saving…' : saveLabel}</button>
        <button disabled={busy} onClick={() => setEditing(null)} style={btn()}>Cancel</button>
      </span>
    </div>
  );

  return (
    <div onClick={() => !busy && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" aria-label={`Timer sessions on ${displayId}`} onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Escape' && !busy) onClose(); }}
        style={{ width: '100%', maxWidth: 820, maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', boxSizing: 'border-box',
          background: T.card, border: `1px solid ${T.inputBorder}`, borderRadius: 16, padding: '20px 22px', color: T.text }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <div style={{ fontSize: 20, fontWeight: 700, flex: 1 }}>Time on {displayId}</div>
          <button onClick={startAdd} disabled={busy || editing === 'new'} style={btn()}>+ Add time</button>
          <button onClick={onClose} disabled={busy} style={btn()}>Done</button>
        </div>
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 14 }}>
          {data?.isManager ? 'Everyone\'s sessions on this ticket.' : 'Your sessions on this ticket. You can change today\'s; ask a manager for earlier days.'}
          {' '}Changes go to Lightspeed straight away.
        </div>

        {error && <div role="alert" style={{ fontSize: 14, color: '#e74c3c', marginBottom: 10 }}>{error}</div>}
        {note && <div role="status" style={{ fontSize: 14, color: note.startsWith('Saved in Spinboard') ? '#e67e22' : '#2ecc71', marginBottom: 10 }}>{note}</div>}

        {editing === 'new' && (
          <div style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid #3498db66`, background: isDark ? '#0f1e30' : '#eef5fc', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Add time worked without the timer</div>
            {editor(() => send('POST', `/api/timer/ticket/${ticketId}/sessions`,
              { startedAt: fromLocalInput(form.start), endedAt: fromLocalInput(form.end), ...(form.userId ? { userId: Number(form.userId) } : {}) },
              'Time added.'), 'Add')}
          </div>
        )}

        {!data ? (
          <div style={{ fontSize: 14, color: T.textSub }}>{error ? '' : 'Loading…'}</div>
        ) : data.sessions.length === 0 ? (
          <div style={{ fontSize: 14, color: T.textSub, padding: '14px 0' }}>No time recorded on this ticket yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.sessions.map(s => (
              <div key={s.id} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 14 }}>
                  {data.isManager && <span style={{ fontWeight: 700, minWidth: 110 }}>{s.name}</span>}
                  <span>{fmtWhen(s.startedAt)} – {s.endedAt ? fmtTime(s.endedAt) : 'running'}</span>
                  <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{s.endedAt ? durationOf(s.startedAt, s.endedAt) : ''}</span>
                  {s.manual && <span style={{ fontSize: 12, color: T.textSub }}>· added by hand</span>}
                  {!s.manual && s.edited && <span style={{ fontSize: 12, color: T.textSub }}>· edited</span>}
                  {s.endReason === 'auto' && <span style={{ fontSize: 12, color: '#e67e22' }}>· stopped automatically</span>}
                  <span style={{ display: 'inline-flex', gap: 8, marginLeft: 'auto' }}>
                    {s.canEdit && editing !== s.id && confirmDelete !== s.id && (
                      <>
                        <button onClick={() => startEdit(s)} disabled={busy} style={btn()}>Edit</button>
                        <button onClick={() => { setConfirmDelete(s.id); setEditing(null); }} disabled={busy} style={{ ...btn(), color: '#e74c3c' }}>Delete</button>
                      </>
                    )}
                    {!s.canEdit && s.endedAt && <span style={{ fontSize: 12, color: T.textSub }} title={s.whyNot || ''}>Locked</span>}
                  </span>
                </div>
                {confirmDelete === s.id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14 }}>Delete this {durationOf(s.startedAt, s.endedAt)}? It comes off the ticket in Lightspeed.</span>
                    <span style={{ display: 'inline-flex', gap: 8, marginLeft: 'auto' }}>
                      <button disabled={busy} onClick={() => send('DELETE', `/api/timer/sessions/${s.id}`, null, 'Session deleted.')} style={btn('danger')}>{busy ? 'Deleting…' : 'Delete'}</button>
                      <button disabled={busy} onClick={() => setConfirmDelete(null)} style={btn()}>Keep it</button>
                    </span>
                  </div>
                )}
                {editing === s.id && editor(() => send('PUT', `/api/timer/sessions/${s.id}`,
                  { startedAt: fromLocalInput(form.start), endedAt: fromLocalInput(form.end) }, 'Session updated.'), 'Save')}
              </div>
            ))}
          </div>
        )}
        {data && data.sessions.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12, fontSize: 15 }}>
            <span style={{ color: T.textSub }}>{data.isManager ? 'Total on this ticket' : 'Your total'}</span>
            <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{durationOf(0, total * 1000)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BenchView({ ticket, theme = 'dark', currentUser = null, onClose, onRunChecklist, backLabel = 'My Workbench' }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const ticketId = ticket.id;

  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [statusMeta, setStatusMeta] = useState([]);
  const [timer, setTimer] = useState(null);            // GET /api/timer/ticket/:id
  const [elsewhere, setElsewhere] = useState(null);    // my timer on another ticket
  const [clockOffset, setClockOffset] = useState(0);   // server minus local, ms
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState('');                // which action is in flight
  const [message, setMessage] = useState(null);        // { text, ok }
  const [requestingEstimate, setRequestingEstimate] = useState(false);
  const [estimateKey, setEstimateKey] = useState(0);
  const [history, setHistory] = useState(null);        // null loading, [] none
  const [historyError, setHistoryError] = useState(false);
  const [openHistory, setOpenHistory] = useState(null);
  const [pickStatus, setPickStatus] = useState('');
  const [showSessions, setShowSessions] = useState(false);
  const messageTimer = useRef(null);

  // Receipt note: what is in the box, what the server last confirmed, and
  // whether a save is under way. saveSeq makes a slow earlier save unable to
  // report "saved" over a newer one.
  const [receiptNote, setReceiptNote] = useState('');
  const [receiptState, setReceiptState] = useState('idle'); // idle | saving | saved | error
  const [receiptExpanded, setReceiptExpanded] = useState(false);
  const savedReceipt = useRef('');
  const receiptDirty = useRef(false);
  const receiptTimer = useRef(null);
  const saveSeq = useRef(0);

  // Internal note: the entry being typed. Saved as a new entry on blur.
  const [newInternal, setNewInternal] = useState('');
  const [internalState, setInternalState] = useState('idle'); // idle | saving | saved | error

  const say = (text, ok = true) => {
    setMessage({ text, ok });
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(null), ok ? 3500 : 8000);
  };

  const applyTimer = useCallback((data) => {
    if (!data) return;
    setTimer(data);
    if (data.serverNow) setClockOffset(new Date(data.serverNow).getTime() - Date.now());
  }, []);

  const loadDetail = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/tickets/${ticketId}`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setDetail(d);
      // Never overwrite what the tech is typing with the server's copy.
      if (!receiptDirty.current) {
        setReceiptNote(d.note || '');
        savedReceipt.current = d.note || '';
      }
      setLoadError('');
      return d;
    } catch (err) {
      setLoadError(err.message || 'Could not load this ticket.');
      return null;
    }
  }, [ticketId]);

  // Adding and editing line items: the Service Queue ticket's own code.
  const lines = useLineItemEditor({ ticketId, detail, setDetail, reload: loadDetail, theme });

  const loadTimer = useCallback(async () => {
    try {
      const [t, me] = await Promise.all([
        fetch(`${API}/api/timer/ticket/${ticketId}`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()),
        fetch(`${API}/api/timer/me`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()),
      ]);
      applyTimer(t);
      setElsewhere(me.running && String(me.workorderId) !== String(ticketId) ? me : null);
      if (me && me.linked === false) setElsewhere({ unlinked: true });
    } catch (_) { /* the timer panel says it could not load */ }
  }, [ticketId, applyTimer]);

  useEffect(() => {
    loadDetail().then(d => {
      const customerId = d?.customer?.id;
      if (!customerId) { setHistory([]); return; }
      fetch(`${API}/api/customers/${customerId}/history`, { credentials: 'include', headers: authHeaders() })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(h => setHistory((h.tickets || [])
          .filter(t => String(t.id) !== String(ticketId) && FINISHED.includes(String(t.status || '').toLowerCase()))
          .slice(0, 5)))
        .catch(() => { setHistoryError(true); setHistory([]); });
    });
    loadTimer();
    fetch(`${API}/api/tickets/status-meta`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json()).then(d => setStatusMeta(Array.isArray(d) ? d : [])).catch(() => {});
  }, [loadDetail, loadTimer, ticketId]);

  // One-second tick only while a timer is running on this ticket.
  const running = !!timer?.me?.running;
  // Paused (or stopped automatically) and not since stopped by its tech.
  const isPaused = !running && !!(timer?.me?.paused || timer?.me?.autoStoppedAt);
  // Anyone's timer running here keeps the ticket total moving too.
  const anyRunning = running || (timer?.techs || []).some(t => t.running);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anyRunning]);

  // ── Timer actions ─────────────────────────────────────────────────────────
  async function timerAction(action, body) {
    setBusy(action);
    try {
      const r = await fetch(`${API}/api/timer/ticket/${ticketId}/${action}`, {
        method: 'POST', credentials: 'include', headers: authHeaders(true),
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { say(data.error || 'That did not work. Try again.', false); await loadTimer(); setBusy(''); return; }
      applyTimer(data);
      setElsewhere(null);
      if (action === 'start' && data.switchedFrom) {
        say(`Paused your timer on SVC-${data.switchedFrom.workorderId} and started this one.`);
      } else if ((action === 'pause' || action === 'stop') && data.written === false) {
        say(`Your time is saved in Spinboard, but Lightspeed did not take it: ${data.writeError || 'unknown error'}. Use Retry.`, false);
      } else if (action === 'pause') say('Paused. Your time is in Lightspeed.');
      else if (action === 'stop') say('Stopped. Your time is in Lightspeed.');
      else if (action === 'retry' && data.written) say('Time sent to Lightspeed.');
      // The time line may have just been created or renamed: show it.
      if (['pause', 'stop', 'retry', 'show-time'].includes(action) || data.switchedFrom) loadDetail();
    } catch (_) {
      say('Could not reach Spinboard. Check the connection and try again.', false);
    }
    setBusy('');
  }

  // ── Receipt note: saves itself ────────────────────────────────────────────
  const saveReceipt = useCallback(async (value) => {
    clearTimeout(receiptTimer.current);
    if (value === savedReceipt.current) { receiptDirty.current = false; return; }
    const seq = ++saveSeq.current;
    setReceiptState('saving');
    try {
      const r = await fetch(`${API}/api/tickets/${ticketId}/note`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(true),
        body: JSON.stringify({ note: value }),
      });
      if (!r.ok) throw new Error();
      if (seq !== saveSeq.current) return;          // a newer save is in flight
      savedReceipt.current = value;
      receiptDirty.current = false;
      setReceiptState('saved');
    } catch (_) {
      if (seq === saveSeq.current) setReceiptState('error');
    }
  }, [ticketId]);

  const onReceiptChange = (value) => {
    setReceiptNote(value);
    receiptDirty.current = true;
    setReceiptState('idle');
    clearTimeout(receiptTimer.current);
    receiptTimer.current = setTimeout(() => saveReceipt(value), RECEIPT_SAVE_DELAY_MS);
  };

  // Leaving the bench with an unsaved change: save it on the way out.
  const latestReceipt = useRef(receiptNote);
  latestReceipt.current = receiptNote;
  useEffect(() => () => {
    clearTimeout(receiptTimer.current);
    if (receiptDirty.current && latestReceipt.current !== savedReceipt.current) saveReceipt(latestReceipt.current);
  }, [saveReceipt]);

  // ── Internal note: a new entry when the box loses focus ───────────────────
  // Same entry format Ticket Detail writes, newest first, so both screens
  // read the internal note the same way.
  async function addInternalNote() {
    const text = newInternal.trim();
    if (!text || internalState === 'saving') return;
    setInternalState('saving');
    try {
      const fresh = await loadDetail();   // never write over a note someone just added
      const d = new Date();
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const who = currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : (currentUser?.username || 'Staff');
      const entry = `[Internal Note — ${who} — ${dateStr} ${timeStr}]\n${text}`;
      const existing = (fresh?.internalNote || '').trim();
      const combined = existing ? `${entry}\n\n${existing}` : entry;
      const r = await fetch(`${API}/api/tickets/${ticketId}/internal-note`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(true),
        body: JSON.stringify({ note: combined }),
      });
      if (!r.ok) throw new Error();
      setNewInternal('');
      setDetail(prev => prev ? { ...prev, internalNote: combined } : prev);
      setInternalState('saved');
    } catch (_) {
      // The text stays in the box, so nothing is lost; tapping away retries.
      setInternalState('error');
    }
  }

  // ── Status ────────────────────────────────────────────────────────────────
  const canFinish = currentUser?.permissions?.finish_ticket !== false;
  const canChangeStatus = currentUser?.permissions?.change_status !== false;
  const statusOptions = [...statusMeta]
    .sort((a, b) => (parseInt(a.sortOrder ?? 99999, 10) - parseInt(b.sortOrder ?? 99999, 10)))
    .filter(s => {
      const n = (s.name || '').toLowerCase();
      if (n.includes('done') && n.includes('paid')) return false;
      if (!canFinish && FINISH_NAMES.includes(n)) return false;
      return true;
    })
    .map(s => ({ id: String(s.id ?? s.workorderStatusID), name: s.name }));
  const currentIdx = statusOptions.findIndex(s => s.name === detail?.status);
  const nextStatus = currentIdx >= 0 ? statusOptions[currentIdx + 1] : null;
  const targetStatus = pickStatus ? statusOptions.find(s => s.id === pickStatus) : nextStatus;

  async function moveStatus() {
    if (!targetStatus) return;
    setBusy('status');
    try {
      const r = await fetch(`${API}/api/tickets/${ticketId}/status`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(true),
        body: JSON.stringify({ workorderStatusID: targetStatus.id }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not change the status.');
      setPickStatus('');
      await loadDetail();
      say(`Moved to ${targetStatus.name}.`);
    } catch (err) { say(err.message, false); }
    setBusy('');
  }

  // ── Derived numbers ───────────────────────────────────────────────────────
  const serverNowMs = now + clockOffset;
  const sessionSecs = running && timer?.me?.startedAt
    ? (serverNowMs - new Date(timer.me.startedAt).getTime()) / 1000 : 0;
  // timer.me.seconds already includes the running session up to serverNow.
  const baseMine = timer?.me?.seconds || 0;
  const sinceFetch = running && timer?.serverNow ? (serverNowMs - new Date(timer.serverNow).getTime()) / 1000 : 0;
  const myTotal = baseMine + Math.max(0, sinceFetch);
  // Everyone's time on the ticket, each running timer counted up live.
  const elapsedSinceFetch = timer?.serverNow ? Math.max(0, (serverNowMs - new Date(timer.serverNow).getTime()) / 1000) : 0;
  const techTimes = (timer?.techs || [])
    .map(t => ({ ...t, live: (t.seconds || 0) + (t.running ? elapsedSinceFetch : 0) }))
    .sort((a, b) => b.live - a.live);
  const ticketTotal = techTimes.reduce((sum, t) => sum + t.live, 0);
  const writeErrors = (timer?.techs || []).filter(t => t.writeError);
  const unlinked = elsewhere?.unlinked;
  const timeLineIds = (timer?.techs || []).map(t => t.lineId).filter(Boolean);
  const checkedOut = lines.checkedOut;

  const displayId = detail?.displayId || ticket.displayId;
  const customerName = detail?.customer?.name || ticket.customer || '';
  const bikeName = (detail?.bike?.description && detail.bike.description !== '—') ? detail.bike.description : (ticket.bike || 'Bike');
  const bikeColor = detail?.bike?.color || '';
  const internalEntries = splitInternalNote(detail?.internalNote);
  const canSeeCustomers = currentUser?.permissions?.customers !== false;

  // ── Styles ────────────────────────────────────────────────────────────────
  const panel = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, minWidth: 0, boxSizing: 'border-box' };
  const label = { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textSub };
  const labelAside = { textTransform: 'none', letterSpacing: 0, fontWeight: 400 };
  const bigBtn = (bg, disabled) => ({
    border: 'none', borderRadius: 12, color: '#fff', background: bg, fontSize: 17, fontWeight: 700,
    minHeight: 48, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    width: '100%', fontFamily: 'inherit',
  });
  const smallBtn = {
    border: `1px solid ${T.inputBorder}`, borderRadius: 8, background: 'transparent', color: T.textSub,
    fontSize: 13, fontWeight: 600, padding: '0 10px', minHeight: 36, cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const textArea = {
    width: '100%', boxSizing: 'border-box', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 10,
    color: T.text, fontSize: 15, lineHeight: 1.5, padding: '10px 12px', outline: 'none', resize: 'none', fontFamily: 'inherit',
  };
  // The header pair (which ticket, and the timer) is drawn in the dark theme
  // whatever the app's theme: it reads as one strong band at the top and the
  // ticket's name stays gold (asked for 2 Oct).
  const H = getTheme('dark');
  // Raised (option A, chosen 2 Oct): a top-to-bottom shade, a lit top edge and
  // a layered shadow lift the pair off the page, which they lacked on light.
  // Each panel carries a coloured left edge, see headerEdge.
  const headerPanel = { ...panel, borderRadius: 14, color: H.text, position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(180deg, #24324f 0%, #18213a 100%)', border: '1px solid #2e3d58',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.10), 0 2px 4px rgba(16,24,40,${isDark ? 0.4 : 0.18}), 0 12px 28px rgba(16,24,40,${isDark ? 0.45 : 0.22})` };
  const headerEdge = (color) => (
    <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, background: color, transition: 'background 0.2s' }} />
  );
  const headerLabel = { ...label, color: H.textSub };
  const headerBtn = { ...smallBtn, border: `1px solid ${H.inputBorder}`, color: H.textSub };
  const HEADER_NAME = '#FFD58A';

  // The facts beside the name in the header.
  const daysOpenNow = detail?.timeIn ? Math.max(0, Math.floor((Date.now() - new Date(detail.timeIn).getTime()) / 86400000)) : null;
  const pastDue = detail?.etaOut && new Date(detail.etaOut).getTime() < Date.now() - 86400000;
  const phone = String(detail?.customer?.phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  const ticketFacts = [
    { label: 'Checked in', value: detail?.timeIn ? fmtDate(detail.timeIn) : '—' },
    { label: 'Due', value: detail?.etaOut ? fmtDate(detail.etaOut) : 'Not set', color: pastDue ? '#ff7b6b' : null },
    { label: 'Open', value: daysOpenNow == null ? '—' : `${daysOpenNow} day${daysOpenNow === 1 ? '' : 's'}` },
    { label: 'Ticket total', value: detail ? `$${Number(detail.totalValue || 0).toFixed(2)}` : '—' },
    { label: 'Phone', value: phone.length === 10 ? `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}` : (detail?.customer?.phone || '—') },
  ];
  const NOTE_HEIGHT = 250;
  const HEADER_MIN = 172;
  // The customer's name stands apart from everything else on the screen: warm
  // gold on dark, the brand's deep blue on light (gold went bronze on white and
  // matched the Pause button, 2 Oct).
  const customerColor = isDark ? '#FFD58A' : '#1f5f99';

  const saveLabel = (state, idleText) => (
    state === 'saving' ? <span style={{ color: T.textSub }}>Saving…</span>
      : state === 'saved' ? <span style={{ color: '#2ecc71', fontWeight: 600 }}>Saved</span>
      : state === 'error' ? <span style={{ color: '#e74c3c', fontWeight: 600 }}>Not saved. Tap away to retry.</span>
      : <span style={{ color: T.textSub }}>{idleText}</span>
  );

  if (loadError && !detail) {
    return (
      <div style={{ padding: 32 }}>
        <button onClick={onClose} style={smallBtn}>← {backLabel}</button>
        <div style={{ ...panel, marginTop: 20, color: '#e74c3c', fontSize: 18 }}>{loadError}</div>
      </div>
    );
  }

  return (
    // marginTop pulls the bench up into the page's top padding (2 Oct: too much
    // space above the header).
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: '100%', marginTop: -20 }}>

      {/* ── Header: which ticket, and the timer ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>

        {/* Who and what this ticket is, readable from across the stand, with the
            facts a tech asks about beside it (2 Oct: the right half was empty).
            Inter, a proportional face, for the large type. */}
        <div style={{ ...headerPanel, minHeight: HEADER_MIN, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {headerEdge('linear-gradient(180deg, #f39c4a, #E67E22)')}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={onClose} style={headerBtn}>← {backLabel}</button>
            <span style={S.statusPill(detail?.status || ticket.status)}>{detail?.status || ticket.status}</span>
            <div style={{ fontSize: 13, color: H.textSub }}>Tech: {detail?.tech || ticket.tech || 'Unassigned'}</div>
          </div>
          {/* The facts box drops below the name on a narrow screen rather than
              squeezing the name and bike into a column a word wide. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', flex: 1 }}>
            {/* Who and what as one unit: the bike sits directly under the
                name, lined up with it (2 Oct: at the bottom of the panel it
                read as a separate fact). */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 12, rowGap: 4, alignItems: 'center', minWidth: 0, flex: '1 1 320px' }}>
              <div style={{ background: '#E67E22', color: '#1A2535', fontFamily: DISPLAY_FONT, fontSize: 20,
                fontWeight: 700, letterSpacing: '0.01em', borderRadius: 10, padding: '4px 10px', lineHeight: 1.1, flexShrink: 0,
                fontVariantNumeric: 'tabular-nums' }}>
                {displayId}
              </div>
              <div style={{ fontFamily: DISPLAY_FONT, fontSize: 30, fontWeight: 700, color: HEADER_NAME, lineHeight: 1.05,
                letterSpacing: '-0.015em', minWidth: 0, overflowWrap: 'anywhere' }}>
                {customerName}
              </div>
              <div />
              <div style={{ fontFamily: DISPLAY_FONT, fontSize: 22, fontWeight: 600, color: '#dfe6f3', lineHeight: 1.15,
                letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>
                {bikeName}{bikeColor ? <><span style={{ color: H.textSub, fontWeight: 400 }}> · </span>{bikeColor}</> : null}
              </div>
            </div>
            <dl style={{ margin: '0 0 0 auto', display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 14, rowGap: 6, fontSize: 13,
              padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: `1px solid ${H.cardBorder}` }}>
              {ticketFacts.map(f => (
                <React.Fragment key={f.label}>
                  <dt style={{ color: H.textSub, whiteSpace: 'nowrap' }}>{f.label}</dt>
                  <dd style={{ margin: 0, color: f.color || H.text, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums' }}>{f.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        </div>

        <div style={{ ...headerPanel, minHeight: HEADER_MIN, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {headerEdge(running ? '#2ecc71' : 'rgba(138,152,181,0.5)')}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <div style={headerLabel}>Your time</div>
            {timer && !unlinked && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 13, color: H.textSub }}>
                  {hm(ticketTotal)} total on this ticket{techTimes.length > 1 ? ` · ${techTimes.length} techs` : ''}
                </div>
                <button onClick={() => setShowSessions(true)} style={headerBtn}>Sessions</button>
              </div>
            )}
          </div>
          {unlinked ? (
            <div style={{ fontSize: 17, color: '#e67e22', lineHeight: 1.5 }}>
              Your login is not linked to a Lightspeed employee, so the timer has nowhere to record your time.
              An administrator can link it in Settings, Users.
            </div>
          ) : !timer ? (
            <div style={{ fontSize: 17, color: H.textSub }}>Loading timer…</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontFamily: DISPLAY_FONT, fontSize: 48, fontWeight: 700, color: running ? '#2ecc71' : H.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {running ? hms(sessionSecs) : hm(myTotal)}
                </div>
                {/* Lit while running (green) or paused (amber), so the state
                    reads from across the stand; quiet otherwise (2 Oct). */}
                <div style={{ fontSize: 13, fontWeight: 700, borderRadius: 999, padding: '3px 10px',
                  color: running ? '#2ecc71' : isPaused ? '#f0a35e' : H.textSub,
                  border: `1px solid ${running ? '#2ecc7155' : isPaused ? '#e67e2288' : H.inputBorder}`,
                  background: running ? '#0f2a15' : isPaused ? '#3a2410' : 'transparent',
                  boxShadow: isPaused ? '0 0 12px rgba(230,126,34,0.35)' : running ? '0 0 12px rgba(46,204,113,0.25)' : 'none' }}>
                  {running ? 'Running' : timer.me.autoStoppedAt ? 'Auto-stopped' : timer.me.paused ? 'Paused' : myTotal > 0 ? 'Stopped' : 'Not started'}
                </div>
              </div>
              {!running && timer.me.autoStoppedAt && (
                <div style={{ fontSize: 14, color: '#f0b060' }}>
                  Your timer was stopped automatically at {new Date(timer.me.autoStoppedAt).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}. A manager will check the time.
                </div>
              )}
              {elsewhere && !running && (
                <div style={{ fontSize: 15, color: '#e67e22' }}>
                  Your timer is running on SVC-{elsewhere.workorderId}. Starting here pauses it.
                </div>
              )}
              {!running && isPaused ? (
                // Paused: pick up where you left off, or say you are done.
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                  <button onClick={() => timerAction('start')} disabled={!!busy} style={bigBtn('#27ae60', !!busy)}>{busy === 'start' ? 'Starting…' : '▶ Continue'}</button>
                  <button onClick={() => timerAction('stop')} disabled={!!busy} style={bigBtn('#c0392b', !!busy)}>{busy === 'stop' ? '…' : '■ Stop'}</button>
                </div>
              ) : !running ? (
                <button onClick={() => timerAction('start')} disabled={!!busy} style={bigBtn('#27ae60', !!busy)}>
                  {busy === 'start' ? 'Starting…' : myTotal > 0 ? '▶ Start again' : '▶ Start'}
                </button>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                  <button onClick={() => timerAction('pause')} disabled={!!busy} style={bigBtn('#b8621a', !!busy)}>{busy === 'pause' ? '…' : '❚❚ Pause'}</button>
                  <button onClick={() => timerAction('stop')} disabled={!!busy} style={bigBtn('#c0392b', !!busy)}>{busy === 'stop' ? '…' : '■ Stop'}</button>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: H.text, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!timer.showTime} disabled={!!busy}
                  onChange={e => timerAction('show-time', { show: e.target.checked })}
                  style={{ width: 20, height: 20, accentColor: '#3498db', flexShrink: 0 }} />
                <span>
                  Show time on invoice{' '}
                  <span style={{ color: H.textSub }}>
                    {timer.showTime ? '· the customer sees "Tech time: 1h 40m"' : '· off for flat-rate jobs'}
                  </span>
                </span>
              </label>
              {writeErrors.length > 0 && (
                <div style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #e74c3c55',
                  background: true ? '#2a1010' : '#fdecea', color: '#e74c3c', fontSize: 15,
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: 200 }}>Time not in Lightspeed: {writeErrors[0].writeError}. It is saved in Spinboard.</span>
                  <button onClick={() => timerAction('retry')} disabled={!!busy}
                    style={{ ...headerBtn, color: '#fff', background: '#e74c3c', border: 'none' }}>Retry</button>
                </div>
              )}
              {techTimes.length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 14, color: H.textSub }}>
                  {techTimes.map(t => (
                    <span key={t.employeeId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {t.running && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2ecc71' }} />}
                      {t.name || 'Someone'}{' '}
                      <span style={{ color: H.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{hm(t.live)}</span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Notes: receipt left, internal right, always the same size ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>

        <div style={{ ...panel, height: NOTE_HEIGHT, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={label}>Receipt note <span style={labelAside}>· prints for the customer</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
              {!checkedOut && saveLabel(receiptState, '')}
              <button onClick={() => setReceiptExpanded(true)} style={smallBtn} aria-label="Open the receipt note full screen">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" /></svg>
                Expand
              </button>
            </div>
          </div>
          <textarea value={receiptNote} readOnly={checkedOut}
            onChange={e => onReceiptChange(e.target.value)}
            onBlur={() => { if (!checkedOut) saveReceipt(receiptNote); }}
            placeholder={checkedOut ? 'This ticket is checked out.' : 'What the customer reads on their receipt.'}
            aria-label="Receipt note"
            style={{ ...textArea, flex: 1, minHeight: 0 }} />
        </div>

        <div style={{ ...panel, height: NOTE_HEIGHT, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={label}>Internal note <span style={labelAside}>· staff only</span></div>
            <div style={{ fontSize: 13 }}>{saveLabel(internalState, 'Saves on tap away')}</div>
          </div>
          <textarea value={newInternal} rows={2}
            onChange={e => { setNewInternal(e.target.value); if (internalState !== 'saving') setInternalState('idle'); }}
            onBlur={addInternalNote}
            placeholder="Enter text here. Saves automatically."
            aria-label="New internal note"
            style={{ ...textArea, flexShrink: 0 }} />
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {internalEntries.length === 0 ? (
              <div style={{ fontSize: 14, color: T.textSub, fontStyle: 'italic', paddingTop: 6 }}>No internal notes yet.</div>
            ) : internalEntries.map((n, i) => (
              <div key={i} style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                {n.who && <div style={{ fontSize: 12, color: T.textSub }}>{n.who} · {n.when}</div>}
                <div style={{ fontSize: 14, lineHeight: 1.45, color: T.text, whiteSpace: 'pre-wrap' }}>{n.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Line items: the Service Queue ticket's own controls ── */}
      <div style={{ ...panel, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ ...label, marginBottom: 10 }}>On the ticket</div>
        {lines.addItemPanel}
        <LineItemsList
          theme={theme}
          large
          title={`Line items (${(detail?.lineItems || []).length})`}
          lineItems={detail?.lineItems || []}
          loadingDetail={!detail}
          checkedOut={checkedOut}
          onQuantityChange={lines.updateItemQuantity}
          onDeleteLineItem={lines.deleteLineItem}
          onEditLineItem={lines.openLineEditor}
          lineStock={lines.lineStock}
          lockedLineIds={timeLineIds}
          lockedNote="Kept up to date by the timer"
        />
        {detail && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 20, fontSize: 17, fontWeight: 700, color: T.text,
            paddingTop: 12, opacity: lines.totalsPending ? 0.45 : 1, transition: 'opacity 0.2s' }}>
            <span style={{ color: T.textSub, fontWeight: 600 }}>Total</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>${Number(detail.totalValue || 0).toFixed(2)}</span>
          </div>
        )}
        {lines.lineEditorModal}
      </div>

      {/* ── Service history: collapsed; greyed out when there is none ── */}
      {(() => {
        const empty = history !== null && history.length === 0;
        return (
          <div style={{ ...panel, display: 'flex', flexDirection: 'column', gap: 8, opacity: empty ? 0.55 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
              <div style={label}>Service history</div>
              {canSeeCustomers && detail?.customer?.id && (
                <button style={{ ...smallBtn, color: '#7fb3e0' }}
                  onClick={() => {
                    sessionStorage.setItem('spinboard-customer-prefill', JSON.stringify({ id: detail.customer.id, name: detail.customer.name }));
                    window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'customers', customerId: detail.customer.id, customerName: detail.customer.name } }));
                  }}>
                  See full customer history →
                </button>
              )}
            </div>
            {history === null ? (
              <div style={{ fontSize: 14, color: T.textSub }}>Loading history…</div>
            ) : empty ? (
              <div style={{ fontSize: 14, color: T.textSub, border: `1px dashed ${T.inputBorder}`, borderRadius: 10, padding: '14px 14px' }}>
                {historyError ? 'Could not load this customer\'s history right now.' : 'No previous work'}
              </div>
            ) : history.map(h => (
              <div key={h.id} style={{ border: `1px solid ${openHistory === h.id ? T.inputBorder : T.border}`, borderRadius: 10 }}>
                <button onClick={() => setOpenHistory(openHistory === h.id ? null : h.id)}
                  aria-expanded={openHistory === h.id}
                  style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                    background: 'transparent', border: 'none', color: T.text, fontSize: 15, padding: '10px 14px', minHeight: 44,
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{fmtDate(h.timeIn)}</span>
                    <span>{h.bike}{h.bikeColor ? ` · ${h.bikeColor}` : ''}</span>
                    {h.tech && h.tech !== 'Unknown' && <span style={{ fontSize: 13, color: T.textSub }}>{h.tech}</span>}
                  </span>
                  <span style={{ display: 'flex', gap: 12, alignItems: 'baseline', color: T.textSub }}>
                    {h.fullTotal != null && <span style={{ fontVariantNumeric: 'tabular-nums', color: T.text, fontWeight: 600 }}>${Number(h.fullTotal).toFixed(2)}</span>}
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{h.displayId} {openHistory === h.id ? '▲' : '▼'}</span>
                  </span>
                </button>
                {openHistory === h.id && (
                  // One row per line, each with its kind, quantity and price, so
                  // a part, its labor and a long labor note no longer run
                  // together as one paragraph (2 Oct). The receipt note sits
                  // apart, labelled.
                  <div style={{ padding: '0 14px 12px', fontSize: 14, color: T.text, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(h.saleLines || []).length > 0 ? (
                      <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
                        {h.saleLines.map((l, i) => {
                          const kind = l.type === 'item' || l.isInventory ? 'Parts' : l.type === 'misc' ? 'Misc' : 'Labor';
                          const kc = HISTORY_KIND[kind];
                          return (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr) auto', alignItems: 'start', gap: 10,
                              padding: '8px 10px', borderTop: i ? `1px solid ${T.border}` : 'none',
                              background: i % 2 ? (isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)') : 'transparent' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, textAlign: 'center', borderRadius: 5, padding: '2px 0', marginTop: 1,
                                color: kc.fg, background: isDark ? kc.bgDark : kc.bg }}>{kind}</span>
                              <span style={{ lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                                {l.quantity > 1 && <span style={{ fontWeight: 700 }}>{l.quantity} × </span>}{l.description}
                              </span>
                              <span style={{ fontVariantNumeric: 'tabular-nums', color: T.textSub, whiteSpace: 'nowrap' }}>
                                {Number.isFinite(l.subtotal) && l.subtotal > 0 ? `$${l.subtotal.toFixed(2)}` : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : <div style={{ color: T.textSub }}>No line detail recorded.</div>}
                    {h.note ? (
                      <div style={{ borderLeft: '3px solid #7fb3e0', padding: '4px 0 4px 10px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textSub, marginBottom: 2 }}>Receipt note</div>
                        <div style={{ whiteSpace: 'pre-wrap', color: T.text, lineHeight: 1.45 }}>{h.note}</div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* An open estimate request on this ticket, for whoever is at the stand. */}
      <EstimateRequestBanner ticketId={ticketId} T={T} refreshKey={estimateKey} />

      {/* ── Action bar, pinned to the bottom ── */}
      <div style={{ position: 'sticky', bottom: 0, marginTop: 'auto', zIndex: 20,
        background: isDark ? '#0d1422' : '#f0f0eb', borderTop: `1px solid ${T.border}`,
        padding: '10px 0 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {message && (
          <div role="status" style={{ borderRadius: 10, padding: '8px 14px', fontSize: 14, fontWeight: 600,
            background: message.ok ? (isDark ? '#0f2a15' : '#d4edda') : (isDark ? '#2a1010' : '#fdecea'),
            color: message.ok ? '#2ecc71' : '#e74c3c', border: `1px solid ${message.ok ? '#2ecc7155' : '#e74c3c55'}` }}>
            {message.text}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={() => onRunChecklist(ticket)} style={{ ...bigBtn('#c8501f', false), flex: '1 1 220px', width: 'auto', fontSize: 16 }}>
            ✓ Run checklist
          </button>
          {/* Request estimate (26 Oct): the tech tells a Manager or Admin the
              customer needs an estimate, and what they found. No status
              change unless the shop set one in Settings. */}
          {currentUser?.permissions?.quotes !== false && (
            <button onClick={() => setRequestingEstimate(true)} style={{ ...bigBtn('#8e5a12', false), flex: '1 1 200px', width: 'auto', fontSize: 16 }}>
              Request estimate
            </button>
          )}
          {canChangeStatus && (
            <>
              <button onClick={moveStatus} disabled={!targetStatus || !!busy}
                style={{ ...bigBtn('#2a7ab8', !targetStatus || !!busy), flex: '1 1 220px', width: 'auto', fontSize: 16 }}>
                {busy === 'status' ? 'Moving…' : targetStatus ? `Move to ${targetStatus.name} →` : 'No next status'}
              </button>
              <select value={pickStatus} onChange={e => setPickStatus(e.target.value)} aria-label="Pick a different status"
                style={{ flex: '0 1 240px', minHeight: 48, fontSize: 15, background: T.input, color: T.text,
                  border: `1px solid ${T.inputBorder}`, borderRadius: 12, padding: '0 14px', fontFamily: 'inherit' }}>
                <option value="">{nextStatus ? `Next: ${nextStatus.name}` : 'Pick a status'}</option>
                {statusOptions.filter(s => s.name !== detail?.status).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {requestingEstimate && (
        <RequestEstimateDialog ticketId={ticketId} T={T} onClose={() => setRequestingEstimate(false)}
          onDone={() => { setRequestingEstimate(false); setEstimateKey(k => k + 1); setMessage({ ok: true, text: 'Estimate requested. A Manager or Admin will see it in Quotes.' }); }} />
      )}

      {showSessions && (
        <SessionsDialog ticketId={ticketId} displayId={displayId} T={T} isDark={isDark}
          onClose={() => setShowSessions(false)}
          onChanged={(d) => { applyTimer(d); loadDetail(); }} />
      )}

      {/* ── Receipt note, expanded ── */}
      {receiptExpanded && (
        <div onClick={() => setReceiptExpanded(false)}
          onKeyDown={e => { if (e.key === 'Escape') setReceiptExpanded(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div role="dialog" aria-modal="true" aria-label="Receipt note" onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 1100, height: 'min(820px, calc(100vh - 48px))', boxSizing: 'border-box',
              background: T.card, border: `1px solid ${T.inputBorder}`, borderRadius: 18, padding: 28,
              display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ background: '#E67E22', color: '#1A2535', fontFamily: "'IBM Plex Mono', monospace", fontSize: 22,
                fontWeight: 500, borderRadius: 8, padding: '4px 12px', flexShrink: 0 }}>{displayId}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: customerColor }}>{customerName}</div>
              <div style={{ fontSize: 20, color: T.textSub }}>{bikeName}{bikeColor ? ` · ${bikeColor}` : ''}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ ...label, fontSize: 15 }}>Receipt note <span style={labelAside}>· prints for the customer</span></div>
              <div style={{ fontSize: 15 }}>{!checkedOut && saveLabel(receiptState, 'Saves as you type')}</div>
            </div>
            <textarea autoFocus value={receiptNote} readOnly={checkedOut}
              onChange={e => onReceiptChange(e.target.value)}
              onBlur={() => { if (!checkedOut) saveReceipt(receiptNote); }}
              placeholder="What the customer reads on their receipt."
              aria-label="Receipt note, expanded"
              style={{ ...textArea, flex: 1, minHeight: 0, fontSize: 22, lineHeight: 1.6, padding: '20px 22px' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ fontSize: 15, color: T.textSub }}>Saves as you type. Closing keeps everything.</div>
              <button onClick={() => setReceiptExpanded(false)}
                style={{ ...bigBtn('#2a7ab8', false), width: 'auto', minWidth: 220, fontSize: 20 }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
